import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { env } from '@/lib/env'
import { readStorage, STORAGE_KEYS } from '@/lib/storage'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)

export const ISO_DATE = 'YYYY-MM-DD'
export const ISO_DATE_TIME = 'YYYY-MM-DD HH:mm:ss'

export type DateInput = string | number | Date | Dayjs | null | undefined

/** The backend stores times in the server timezone (EST). */
export function fromServer(value: DateInput): Dayjs | null {
  if (!value || value === '0000-00-00 00:00:00') return null
  const parsed = dayjs.tz(value, env.serverTimezone)
  return parsed.isValid() ? parsed : null
}

export function userTimezone(): string {
  const stored = readStorage(STORAGE_KEYS.timezone)
  if (stored && stored !== 'none') return stored
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export function toIsoDate(value: DateInput): string {
  return value ? dayjs(value).format(ISO_DATE) : ''
}

export function toIsoDateTime(value: DateInput): string {
  return value ? dayjs(value).format(ISO_DATE_TIME) : ''
}

/** "Oct 14, 2026" */
export function formatDate(value: DateInput): string {
  const d = fromServer(value)
  return d ? d.tz(userTimezone()).format('MMM D, YYYY') : '—'
}

/** "Oct 14" */
export function formatShortDate(value: DateInput): string {
  const d = fromServer(value)
  return d ? d.tz(userTimezone()).format('MMM D') : '—'
}

/** "Oct 14, 3:51 PM" */
export function formatDateTime(value: DateInput): string {
  const d = fromServer(value)
  return d ? d.tz(userTimezone()).format('MMM D, h:mm A') : '—'
}

/** "Wed, Oct 14 · 11:59 PM EST" — used for due dates, which are always stored in EST. */
export function formatDueDate(value: DateInput): string {
  const d = fromServer(value)
  if (!d) return '—'
  const local = d.tz(userTimezone())
  return `${local.format('ddd, MMM D · h:mm A')} ${zoneAbbr(local)}`
}

export function zoneAbbr(d: Dayjs): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: userTimezone(), timeZoneName: 'short' }).formatToParts(d.toDate())
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
}

export function formatRelative(value: DateInput): string {
  const d = fromServer(value)
  return d ? d.fromNow() : '—'
}

/** Returns a human countdown such as "2d left", "18h left", "Overdue" for a due date. */
export function timeLeft(value: DateInput): { label: string; overdue: boolean; dueToday: boolean } {
  const d = fromServer(value)
  if (!d) return { label: '—', overdue: false, dueToday: false }
  const now = dayjs()
  const diffMinutes = d.diff(now, 'minute')
  if (diffMinutes < 0) return { label: 'Overdue', overdue: true, dueToday: false }
  const dueToday = d.tz(userTimezone()).isSame(now.tz(userTimezone()), 'day')
  if (diffMinutes < 60) return { label: `${diffMinutes}m left`, overdue: false, dueToday }
  const hours = Math.floor(diffMinutes / 60)
  if (hours < 48) return { label: `${hours}h left`, overdue: false, dueToday }
  return { label: `${Math.floor(hours / 24)}d left`, overdue: false, dueToday }
}

export function startOfMonth(): Dayjs {
  return dayjs().startOf('month')
}

export function today(): Dayjs {
  return dayjs()
}

export { dayjs }
